'use client';

import { createContext, useCallback, useContext } from 'react';
import { analytics } from '@/lib/analytics';

export interface ToneSurface {
  /**
   * Which row the card is sitting in. Shares one vocabulary with
   * `tone.played`'s long-standing `from` values (`search_page`,
   * `profile_page`, `tone_page_linked`, `tone_embed`), so plays, impressions
   * and click-throughs on the same row line up.
   */
  surface: string;
  /** Row-level context, merged into every event the row emits. */
  properties?: Record<string, unknown>;
  /** Extra properties for one card's click, e.g. its embedding distance. */
  cardProperties?: (toneId: number, position: number) => Record<string, unknown> | undefined;
}

/**
 * Context rather than props, because a card sits several components below the
 * row that knows which surface it is (ToneSection → ToneCardRow →
 * ToneCardStack → TonePlayer) and none of the components in between have any
 * other use for it. Undefined outside a tracked row, which is how untracked
 * surfaces stay silent instead of reporting an empty surface.
 */
export const ToneSurfaceContext = createContext<ToneSurface | undefined>(undefined);

export const useToneSurface = () => useContext(ToneSurfaceContext);

/** Marks a card that ToneRowTracking should count as rendered. */
export const TONE_CARD_SELECTOR = '[data-tone-card]';

/**
 * Click-through tracking for one tone card, to be spread onto the card's root.
 *
 * Capture phase, not the card's own click handler: that one deliberately does
 * nothing when the click came from a link, because the links navigate
 * themselves — so it never sees a click on the image or the title, which is
 * most of them.
 *
 * `href` is the card's own destination. Comparing the clicked anchor against it
 * is what separates a click-through from the avatar and username links, which
 * lead to a profile. Matching on the card's real href rather than a `/tones/`
 * prefix keeps this correct in the partner select flow, where cards point at
 * `/api/v1/select/tones/...`.
 */
export const useToneCardTracking = (
  toneId: number | null,
  position: number | undefined,
  href: string | undefined
) => {
  const context = useToneSurface();
  const { surface, properties, cardProperties } = context ?? {};

  const onClickCapture = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (toneId == null) return;
      const target = event.target as HTMLElement;

      // Buttons (the players) don't navigate anywhere.
      if (target.closest('button')) return;
      const link = target.closest('a');
      if (link && link.getAttribute('href') !== href) return;

      analytics.capture('tone.card.clicked', {
        surface,
        // Row context first: tone_id always means the card that was clicked.
        ...properties,
        tone_id: toneId,
        position,
        ...(position === undefined ? undefined : cardProperties?.(toneId, position)),
      });
    },
    [toneId, position, href, surface, properties, cardProperties]
  );

  // `data-tone-card` marks a tracked card for the row's impression count. Part
  // of the same spread so it can never drift from what the click handler covers.
  return surface ? { onClickCapture, 'data-tone-card': '' } : {};
};
